import React, { useEffect, forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  classNames,
  useGlobalId,
  useUpdateRef,
  useSetState
} from '@zambezi/sdk/common'
import { Lookup } from '@zambezi/sdk/dropdown-list'
import {
  getError,
  errorOptionsType,
  hintOptionsType,
  getInputProps
} from './zambezi-helpers'
import Label from './Label'

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefRenderFunction<React.Ref<HTMLInputElement>, import('./interfaces').LookupFieldProps<T, Item>>}
 * @param {import('./interfaces').LookupFieldProps<T, Item>} props
 * @param {React.Ref<HTMLInputElement>} innerRef
 */
function lookupField (props, innerRef) {
  const {
    id: propsId,
    name,
    label,
    value: propValue,
    values,
    defaultValue,
    className,
    autoFocus,
    rules,
    error: propError = '',
    errorOptions = {},
    errorMessageShown = (error, isChanged) => error && !isChanged,
    disabled,
    hint,
    hintOptions,
    inputProps = {},
    customRenderer,
    onChange,
    onBlur,
    onFocus,
    errorInline,
    withAsterisk
  } = props

  const elementRef = useRef()
  const errorId = useGlobalId('zb-formidable-error-')
  const globalId = useGlobalId('zb-control-id-')
  const id = propsId || globalId
  const [state, setState] = useSetState({
    isFocused: false,
    isChanged: false,
    value: propValue || defaultValue,
    error: '',
    suggestions: values
  })
  const { isFocused, isChanged, value, error } = state
  const { inputOptions, labelOptions, errorMessage } = renderOptions()

  useEffect(componentWillReceiveProps, [propValue, values])
  useUpdateRef(innerRef, elementRef, { ...props })

  return (
    <div className='zb-control-wrap'>
      {label ? (
        <Label {...labelOptions}>
          {label} {withAsterisk && <sup className='zb-asterisk'>*</sup>}
        </Label>
      ) : null}
      <div className={classNames('zb-control', { 'is-error': error })}>
        <div className={className}>
          <input
            type='hidden'
            ref={elementRef}
            name={name}
            rules={rules}
            value={value || ''}
          />
          {customRenderer ? (
            customRenderer(inputOptions)
          ) : (
            <Lookup {...inputOptions} />
          )}
          {errorMessage}
        </div>
      </div>
    </div>
  )

  function componentWillReceiveProps () {
    setState({
      value: propValue,
      suggestions: values
    })
  }

  function handleChange (event, { label }) {
    setState({
      value: label,
      isChanged: true
    })
    onChange(name, value)
  }

  function handleBlur () {
    setState({
      isFocused: false
    })
    !disabled && onBlur(name, value)
  }

  function handleFocus () {
    setState({
      isChanged: false,
      isFocused: true
    })
    !disabled && onFocus(name, value)
  }

  function handleInputChange (event, data) {
    const { value } = data

    handleChange(event, data)
    onChange(name, value)
  }

  function suggestionsFetchRequested ({ value }) {
    const inputValue = value.trim().toLowerCase()

    const filteredSuggestions = values.filter(
      s => inputValue && s.label.toLowerCase().indexOf(inputValue) === 0
    )

    setState({
      suggestions: filteredSuggestions
    })
  }

  function suggestionsClearRequested () {
    setState({
      suggestions: []
    })
  }

  function renderOptions () {
    const errorMessage = errorMessageShown(propError, isChanged, isFocused)
      ? getError(propError, errorOptions, errorId, errorInline)
      : null

    const inputOptions = {
      value,
      id,
      getSuggestionValue: ({ label }) => label,
      onSuggestionSelected: (event, { suggestion }) => {
        handleChange(event, suggestion)
      },
      suggestionsFetchRequested,
      suggestionsClearRequested,
      ...inputProps,
      onBlur: disabled ? noop : handleBlur,
      onFocus: disabled ? noop : handleFocus,
      onChange: disabled ? noop : handleInputChange,
      className: classNames({ 'zb-is-error': !!propError }, className),
      suggestions: state.suggestions,
      rules,
      inputProps: {
        ...getInputProps(inputProps),
        'aria-invalid': !!propError,
        'aria-describedby': errorMessage && errorMessage.props.id
      }
    }

    const labelOptions = {
      inputOptions,
      hint,
      hintOptions,
      label,
      error: propError,
      errorOptions
    }

    if (autoFocus) inputOptions.autoFocus = autoFocus
    if (disabled) inputOptions.isDisabled = disabled
    if (propError) inputOptions.isError = true

    return { inputOptions, labelOptions, errorMessage }
  }
}

function noop () {}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').LookupFieldProps<T, Item> & React.RefAttributes<HTMLInputElement>>}
 */
const LookupField = forwardRef(lookupField)

LookupField.displayName = 'LookupField'

LookupField.propTypes = {
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.any,
  errorOptions: errorOptionsType,
  errorMessageShown: PropTypes.func,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  className: PropTypes.string,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  hint: PropTypes.string,
  hintOptions: hintOptionsType,
  values: PropTypes.array.isRequired,
  inputProps: PropTypes.object,
  errorInline: PropTypes.bool,
  withAsterisk: PropTypes.bool
}

LookupField.defaultProps = {
  errorOptions: { arrowDirection: 'top' },
  onFocus: noop,
  onChange: noop,
  onBlur: noop,
  errorMessageShown: (error, isChanged) => error && !isChanged,
  values: [],
  withAsterisk: false
}

export default LookupField
